import { useState } from 'react';
import { projects } from '@/mocks/projects';
import { Link } from 'react-router-dom';
import { projectPathById } from '@/content/projectPages';

const textureImages: Record<string, string> = {
  'texture-concrete': "/images/texture-concrete.webp",
  'texture-stone': "/images/ai-warm-cream-limestone-surface-texture-with-sub-ed190c.webp",
  'texture-terrazzo': "/images/ai-warm-cream-terrazzo-surface-texture-with-fine-0e6e6d.webp",
  'texture-timber': "/images/ai-fine-oak-hardwood-timber-grain-surface-textur-0fa527.webp",
  'texture-dark': "/images/ai-abstract-dark-charcoal-near-black-texture-wit-842819.webp",
};

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ photos: string[]; index: number } | null>(null);

  const filters = ['All', 'Residential', 'Commercial', 'Digital'];

  const filtered = activeFilter === 'All'
    ? projects
    : projects.filter((p) => p.category === activeFilter);

  const getImageUrl = (imageSlot: string, photos?: string[]) => {
    if (photos && photos.length > 0) {
      return photos[0];
    }
    return textureImages[imageSlot] || textureImages['texture-concrete'];
  };

  const openLightbox = (photos: string[], index: number) => {
    setLightbox({ photos, index });
  };

  const nextLightbox = () => {
    if (lightbox) {
      setLightbox({
        photos: lightbox.photos,
        index: (lightbox.index + 1) % lightbox.photos.length,
      });
    }
  };

  const prevLightbox = () => {
    if (lightbox) {
      setLightbox({
        photos: lightbox.photos,
        index: (lightbox.index - 1 + lightbox.photos.length) % lightbox.photos.length,
      });
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="pt-32 pb-12 md:pt-40 md:pb-16 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <p className="font-mono text-xs text-stone-400 tracking-[0.25em] uppercase mb-5 whitespace-nowrap">
          Our Projects
        </p>
        <h1 className="font-serif text-5xl md:text-7xl text-charcoal-900 mb-12">
          Melbourne Building Design Portfolio
        </h1>

        <div className="flex flex-wrap gap-2 mb-14 pb-6 border-b border-stone-200">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-5 py-2 text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeFilter === f
                  ? 'bg-charcoal-900 text-cream-50'
                  : 'text-stone-500 hover:text-charcoal-800 hover:bg-stone-100'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => {
            const hasPhotos = project.photos && project.photos.length > 0;
            const photoCount = hasPhotos ? (project.photos as string[]).length : 0;

            return (
              <div
                key={project.id}
                className={`group bg-cream-50 border transition-all duration-400 cursor-pointer ${
                  project.featured ? 'border-terracotta-300 ring-1 ring-terracotta-200' : 'border-stone-200 hover:border-stone-300'
                }`}
                onClick={() => setExpandedId(expandedId === project.id ? null : project.id)}
              >
                <div className="relative w-full aspect-[4/3] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-editorial group-hover:scale-105"
                    style={{ backgroundImage: `url('${getImageUrl(project.imageSlot, project.photos as string[] | undefined)}')` }}
                  />
                  <div className="absolute inset-0 bg-charcoal-900/5 group-hover:bg-charcoal-900/20 transition-colors duration-500"></div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="px-5 py-2.5 border border-cream-50/50 text-cream-50 text-[11px] tracking-widest uppercase whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                  {project.featured && (
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 text-xs font-medium tracking-wide bg-terracotta-500 text-cream-50 whitespace-nowrap">
                        Featured
                      </span>
                    </div>
                  )}
                  {hasPhotos && (
                    <div className="absolute bottom-3 right-3">
                      <span className="inline-block px-2 py-0.5 text-[9px] tracking-widest uppercase bg-charcoal-900/65 text-cream-100/75 whitespace-nowrap">
                        {photoCount} photos
                      </span>
                    </div>
                  )}
                  {!hasPhotos && (
                    <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <span className="inline-block px-2 py-0.5 text-[9px] tracking-widest uppercase bg-charcoal-900/65 text-cream-100/75 whitespace-nowrap">
                        Photo soon
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] text-stone-400 tracking-wide whitespace-nowrap">
                      {project.category}
                    </span>
                    {(project as any).subLabel && (
                      <span className="text-[11px] text-terracotta-500 tracking-wide uppercase whitespace-nowrap">
                        {(project as any).subLabel}
                      </span>
                    )}
                  </div>
                  {projectPathById[project.id] ? (
                    <h3 className="font-serif text-xl text-charcoal-900 mb-1 group-hover:text-terracotta-600 transition-colors duration-300">
                      <Link to={projectPathById[project.id]} onClick={(event) => event.stopPropagation()}>{project.name}</Link>
                    </h3>
                  ) : (
                    <h3 className="font-serif text-xl text-charcoal-900 mb-1">{project.name}</h3>
                  )}
                  <p className="text-xs text-stone-500 mb-3">{project.suburb}</p>
                  <p className="text-xs text-terracotta-600 font-medium mb-3 leading-relaxed">
                    {project.credential}
                  </p>

                  <div
                    className={`grid transition-all duration-500 ease-editorial ${
                      expandedId === project.id ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="pt-4 border-t border-stone-200">
                        <p className="text-sm text-stone-600 leading-relaxed mb-4">{project.description}</p>

                        {hasPhotos && (
                          <div className="mb-4">
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                              {(project.photos as string[]).map((photo, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLightbox(project.photos as string[], idx);
                                  }}
                                  className="flex-shrink-0 w-20 h-16 md:w-24 md:h-18 overflow-hidden cursor-pointer border border-stone-200 hover:border-terracotta-400 transition-colors duration-300"
                                >
                                  <img
                                    src={photo}
                                    alt={`${project.name} — photo ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1.5">
                          {project.tags.map((t) => (
                            <span key={t} className="px-2.5 py-0.5 text-[11px] text-stone-500 bg-stone-100 whitespace-nowrap">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <button className="mt-4 text-xs text-stone-400 hover:text-terracotta-500 transition-colors duration-300 cursor-pointer flex items-center gap-1 whitespace-nowrap">
                    {expandedId === project.id ? 'Show less' : 'Read more'}
                    <i className={`text-sm transition-transform duration-300 ${expandedId === project.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-charcoal-950/95 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 text-cream-50 text-2xl cursor-pointer z-50 w-10 h-10 flex items-center justify-center hover:text-terracotta-400 transition-colors duration-300"
            onClick={() => setLightbox(null)}
          >
            <i className="ri-close-line"></i>
          </button>

          <button
            className="absolute left-4 md:left-8 text-cream-50 text-2xl cursor-pointer z-50 w-12 h-12 flex items-center justify-center bg-charcoal-900/50 hover:bg-charcoal-900/80 transition-colors duration-300 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              prevLightbox();
            }}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <button
            className="absolute right-4 md:right-8 text-cream-50 text-2xl cursor-pointer z-50 w-12 h-12 flex items-center justify-center bg-charcoal-900/50 hover:bg-charcoal-900/80 transition-colors duration-300 rounded-full"
            onClick={(e) => {
              e.stopPropagation();
              nextLightbox();
            }}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] w-full flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.photos[lightbox.index]}
              alt={`Project photo ${lightbox.index + 1} of ${lightbox.photos.length}`}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-cream-50 text-xs tracking-wider">
            {lightbox.index + 1} / {lightbox.photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
